/*
 * ATN Country Intelligence - map zoom
 *
 * Gives the country map image simple zoom and pan without loading a mapping
 * library. The image is scaled with a CSS transform and can be dragged once
 * it is zoomed in.
 *
 * Markup contract:
 *   <div data-atn-map data-map-max="3" data-map-step="0.5">
 *     <div data-atn-map-canvas>... image and marker ...</div>
 *     <button data-atn-map-zoom="in">  <button data-atn-map-zoom="out">
 *   </div>
 */
(function () {
  'use strict';

  if (window.atnCiMap) return;
  window.atnCiMap = true;

  function MapView(root) {
    this.root = root;
    this.canvas = root.querySelector('[data-atn-map-canvas]');
    if (!this.canvas) return;

    this.max = parseFloat(root.getAttribute('data-map-max')) || 3;
    this.step = parseFloat(root.getAttribute('data-map-step')) || 0.5;
    this.min = 1;

    // Where the map should sit when the page loads
    this.startScale = Math.min(this.max, Math.max(this.min,
      parseFloat(root.getAttribute('data-map-start')) || 1));
    this.focusX = parseFloat(root.getAttribute('data-map-fx'));
    this.focusY = parseFloat(root.getAttribute('data-map-fy'));
    if (isNaN(this.focusX)) this.focusX = 50;
    if (isNaN(this.focusY)) this.focusY = 50;

    this.scale = this.startScale;
    this.x = 0;
    this.y = 0;

    this.dragging = false;
    this.startX = 0;
    this.startY = 0;

    this.bind();
    this.focus();
    this.render();

    // The frame has no size until layout settles, so re-centre once it does
    var self = this;
    window.requestAnimationFrame(function () {
      self.focus();
      self.render();
    });
  }

  /* Move the focus point to the middle of the frame at the current scale */
  MapView.prototype.focus = function () {
    if (this.scale <= 1) {
      this.x = 0;
      this.y = 0;
      return;
    }
    var rect = this.root.getBoundingClientRect();
    this.x = (0.5 - this.focusX / 100) * rect.width * this.scale;
    this.y = (0.5 - this.focusY / 100) * rect.height * this.scale;
    this.clamp();
  };

  MapView.prototype.bind = function () {
    var self = this;

    this.root.querySelectorAll('[data-atn-map-zoom]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var dir = btn.getAttribute('data-atn-map-zoom') === 'in' ? 1 : -1;
        self.zoom(dir);
      });
    });

    this.canvas.addEventListener('pointerdown', function (e) {
      if (self.scale <= 1) return;
      self.dragging = true;
      self.startX = e.clientX - self.x;
      self.startY = e.clientY - self.y;
      self.canvas.setPointerCapture(e.pointerId);
      self.root.classList.add('is-dragging');
    });

    this.canvas.addEventListener('pointermove', function (e) {
      if (!self.dragging) return;
      self.x = e.clientX - self.startX;
      self.y = e.clientY - self.startY;
      self.clamp();
      self.render();
    });

    ['pointerup', 'pointercancel'].forEach(function (evt) {
      self.canvas.addEventListener(evt, function () {
        self.dragging = false;
        self.root.classList.remove('is-dragging');
      });
    });

    // Keyboard support on the zoom buttons is native; arrow keys pan
    this.root.addEventListener('keydown', function (e) {
      var pan = 30;
      if (e.key === 'ArrowLeft') { self.x += pan; }
      else if (e.key === 'ArrowRight') { self.x -= pan; }
      else if (e.key === 'ArrowUp') { self.y += pan; }
      else if (e.key === 'ArrowDown') { self.y -= pan; }
      else { return; }
      e.preventDefault();
      self.clamp();
      self.render();
    });
  };

  MapView.prototype.zoom = function (dir) {
    var next = this.scale + dir * this.step;
    this.scale = Math.min(this.max, Math.max(this.min, Math.round(next * 100) / 100));
    if (this.scale <= 1) { this.x = 0; this.y = 0; }
    this.clamp();
    this.render();
  };

  /* Keep the image covering the frame so no empty gap appears at the edges */
  MapView.prototype.clamp = function () {
    var rect = this.root.getBoundingClientRect();
    var limitX = (rect.width * (this.scale - 1)) / 2;
    var limitY = (rect.height * (this.scale - 1)) / 2;
    this.x = Math.min(limitX, Math.max(-limitX, this.x));
    this.y = Math.min(limitY, Math.max(-limitY, this.y));
  };

  MapView.prototype.render = function () {
    this.canvas.style.transform =
      'translate(' + this.x + 'px, ' + this.y + 'px) scale(' + this.scale + ')';
    // Lets the marker cancel the zoom so it keeps a constant size
    this.root.style.setProperty('--cif-map-scale', this.scale);
    this.root.classList.toggle('is-zoomed', this.scale > 1);

    var out = this.root.querySelector('[data-atn-map-zoom="out"]');
    var inn = this.root.querySelector('[data-atn-map-zoom="in"]');
    if (out) out.classList.toggle('is-disabled', this.scale <= this.min);
    if (inn) inn.classList.toggle('is-disabled', this.scale >= this.max);
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-atn-map]').forEach(function (el) {
      if (el.dataset.mapReady === '1') return;
      el.dataset.mapReady = '1';
      new MapView(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();